/**
 * VireoHR - Manager Attendance Screen
 * FIXED: Now uses real Firestore queries instead of mock data
 * View daily attendance for assigned locations
 */

import { collection, query, where, getDocs } from 'firebase/firestore';
import React, { useState, useEffect } from 'react';

import { Badge } from '@/components/Badge';
import { Card } from '@/components/Card';
import Layout from '@/components/Layout';
import { StatusDot } from '@/components/StatusDot';
import { useAuth } from '@/contexts/AuthContext';
import { firestore } from '@/lib/firebase/client';

interface AttendanceRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  locationName: string;
  status: 'CLOCKED_IN' | 'CLOCKED_OUT' | 'LATE' | 'ON_TIME' | 'MISSING' | 'NO_SHOW';
  clockInTime?: string;
  clockOutTime?: string;
  lateMinutes: number;
}

interface Branch {
  id: string;
  name: string;
}

interface UserData {
  id: string;
  name: string;
  locationIds?: string[];
}

export default function ManagerAttendance() {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [branches, setBranches] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.tenantId) {
      fetchAttendance();
    }
  }, [selectedDate, user?.tenantId]);

  const fetchAttendance = async () => {
    if (!user?.tenantId) return;

    try {
      setLoading(true);

      // First, fetch all branches
      const branchesRef = collection(firestore, 'branches');
      const branchesQuery = query(
        branchesRef,
        where('tenantId', '==', user.tenantId)
      );
      const branchesSnapshot = await getDocs(branchesQuery);
      
      const branchesMap: Record<string, string> = {};
      branchesSnapshot.docs.forEach(doc => {
        branchesMap[doc.id] = doc.data().name || 'Unknown Branch';
      });
      setBranches(branchesMap);

      // Fetch all employees for this tenant
      const usersRef = collection(firestore, 'users');
      const usersQuery = query(
        usersRef,
        where('tenantId', '==', user.tenantId),
        where('active', '==', true)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      const employeesMap: Record<string, UserData> = {};
      usersSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.role !== 'owner') { // Exclude owners
          employeesMap[doc.id] = {
            id: doc.id,
            name: data.name || 'Unknown',
            locationIds: data.locationIds || [],
          };
        }
      });

      // Calculate date range for the selected date
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      // Fetch attendance records for the selected date
      const attendanceRef = collection(firestore, 'attendance');
      const attendanceQuery = query(
        attendanceRef,
        where('tenantId', '==', user.tenantId),
        where('clockInTimestamp', '>=', startOfDay.toISOString()),
        where('clockInTimestamp', '<=', endOfDay.toISOString())
      );
      
      const attendanceSnapshot = await getDocs(attendanceQuery);

      // Build attendance records with employee names
      const attendanceRecords: AttendanceRecord[] = [];
      const employeesWithAttendance = new Set<string>();

      attendanceSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const employeeId = data.employeeId;
        const employee = employeesMap[employeeId];
        
        if (employee) {
          employeesWithAttendance.add(employeeId);
          
          // Determine status
          let status: AttendanceRecord['status'] = 'CLOCKED_IN';
          if (data.clockOutTimestamp) {
            status = 'CLOCKED_OUT';
          }
          if (data.status === 'LATE') {
            status = 'LATE';
          } else if (data.status === 'ON_TIME') {
            status = data.clockOutTimestamp ? 'CLOCKED_OUT' : 'CLOCKED_IN';
          }

          // Filter by manager's branches if assigned
          const branchId = data.branchId || (employee.locationIds && employee.locationIds[0]);
          const managerBranchIds = user?.managerLocationIds || [];
          
          if (managerBranchIds.length === 0 || managerBranchIds.includes(branchId)) {
            attendanceRecords.push({
              id: doc.id,
              employeeId: employeeId,
              employeeName: employee.name,
              locationName: branchesMap[branchId] || 'Unknown Branch',
              status: status,
              clockInTime: data.clockInTimestamp 
                ? new Date(data.clockInTimestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : undefined,
              clockOutTime: data.clockOutTimestamp
                ? new Date(data.clockOutTimestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : undefined,
              lateMinutes: data.lateMinutes || 0,
            });
          }
        }
      });

      // Check for missing employees (those scheduled but not clocked in)
      // This would require checking schedules - simplified version shows employees without attendance
      const managerBranchIds = user?.managerLocationIds || [];
      
      Object.values(employeesMap).forEach(employee => {
        if (!employeesWithAttendance.has(employee.id)) {
          // Check if employee belongs to manager's branches
          const employeeBranchId = employee.locationIds && employee.locationIds[0];
          
          if (managerBranchIds.length === 0 || (employeeBranchId && managerBranchIds.includes(employeeBranchId))) {
            // For now, we don't add MISSING status without checking schedules
            // This could be enhanced by checking if employee had a schedule for this day
          }
        }
      });

      // Sort by status (late first, then clocked in, then clocked out)
      attendanceRecords.sort((a, b) => {
        const statusOrder = { 'LATE': 0, 'CLOCKED_IN': 1, 'MISSING': 2, 'CLOCKED_OUT': 3, 'ON_TIME': 1, 'NO_SHOW': 2 };
        return (statusOrder[a.status] || 99) - (statusOrder[b.status] || 99);
      });

      setAttendance(attendanceRecords);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'CLOCKED_OUT':
        return <Badge variant="success">Completed</Badge>;
      case 'CLOCKED_IN':
      case 'ON_TIME':
        return <Badge variant="info">Working</Badge>;
      case 'LATE':
        return <Badge variant="warning">Late</Badge>;
      case 'MISSING':
      case 'NO_SHOW':
        return <Badge variant="error">Missing</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const completedCount = attendance.filter(a => a.status === 'CLOCKED_OUT').length;
  const workingCount = attendance.filter(a => a.status === 'CLOCKED_IN' || a.status === 'ON_TIME').length;
  const lateCount = attendance.filter(a => a.status === 'LATE').length;
  const missingCount = attendance.filter(a => a.status === 'MISSING' || a.status === 'NO_SHOW').length;

  if (loading) {
    return (
      <Layout title="Attendance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#0D9488] border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Attendance">
      <div className="p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-[#0D9488]">Daily Attendance</h1>
        </div>

        {/* Date Picker */}
        <Card>
          <div className="flex items-center gap-3">
            <label className="font-semibold text-[#0D9488]">📅 Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0D9488]"
            />
          </div>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="text-center">
            <p className="text-2xl font-bold text-[#0D9488]">{completedCount}</p>
            <p className="text-xs text-gray-500">Completed</p>
          </Card>
          <Card className="text-center">
            <p className="text-2xl font-bold text-blue-600">{workingCount}</p>
            <p className="text-xs text-gray-500">Working</p>
          </Card>
          <Card className="text-center">
            <p className="text-2xl font-bold text-amber-600">{lateCount}</p>
            <p className="text-xs text-gray-500">Late</p>
          </Card>
          <Card className="text-center">
            <p className="text-2xl font-bold text-red-600">{missingCount}</p>
            <p className="text-xs text-gray-500">Missing</p>
          </Card>
        </div>

        {/* Attendance List */}
        <div className="space-y-3">
          {attendance.map((record) => (
            <Card key={record.id} className="hover:shadow-md transition">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <StatusDot status={record.status === 'LATE' ? 'LATE' : 'ON_TIME'} />
                  <div>
                    <p className="font-semibold text-[#0D9488]">{record.employeeName}</p>
                    <p className="text-sm text-gray-600">{record.locationName}</p>
                  </div>
                </div>
                <div className="text-right">
                  {getStatusBadge(record.status)}
                  {record.clockInTime && (
                    <p className="text-sm text-gray-600 mt-1">
                      In: {record.clockInTime}
                      {record.clockOutTime && ` | Out: ${record.clockOutTime}`}
                    </p>
                  )}
                  {record.lateMinutes > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      {record.lateMinutes} min late
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {attendance.length === 0 && (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📊</div>
              <p className="text-gray-500">No attendance records for this date</p>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
